import { useMemo } from 'react';
import { DefaultTheme, useTheme } from 'styled-components';

import { Badge } from '@/core/components/Badge';

import { ReportScore } from '@/features/Reports/components/Source/Source.types';
import { mapSourceCredibilityToReportScore } from '@/features/Reports/helpers';
import { buildMediaProxyUrl } from '@/features/Reports/media-proxy';

import { getTimeAndDate } from '@/utils/helpers';

import {
  Body,
  CardLink,
  Header,
  Thumbnail,
  ThumbnailImage,
  TimeAgo,
  Title,
} from './ArticleCard.styles';
import { ArticleCardProps } from './ArticleCard.types';

const getReliabilityColor = (
  score: ReportScore,
  reliability: DefaultTheme['colors']['reliability']
) => {
  switch (score) {
    case ReportScore.HIGH:
      return reliability.high;
    case ReportScore.MEDIUM:
      return reliability.medium;
    case ReportScore.LOW:
      return reliability.low;
    default:
      return reliability.unknown;
  }
};

export const ArticleCard = ({
  url,
  title,
  name,
  credibility,
  publishedDatetime,
  image,
}: ArticleCardProps) => {
  const { colors } = useTheme();

  const badgeColor = useMemo(
    () => getReliabilityColor(mapSourceCredibilityToReportScore(credibility), colors.reliability),
    [credibility, colors.reliability]
  );

  const timeAgo = publishedDatetime
    ? getTimeAndDate({
        dateStr: publishedDatetime,
        fromNow: true,
        fromNowSuffix: true,
      })
    : null;

  const imageSrc = buildMediaProxyUrl(image);

  return (
    <CardLink
      href={url}
      target="_blank"
      rel="noreferrer noopener"
    >
      <Thumbnail>
        {imageSrc && (
          <ThumbnailImage
            src={imageSrc}
            alt={name}
          />
        )}
      </Thumbnail>
      <Body>
        <Header>
          <Title>{title}</Title>
          {timeAgo && <TimeAgo color={colors.secondaryText}>{timeAgo}</TimeAgo>}
        </Header>
        <Badge
          text={name}
          backgroundColor={badgeColor}
          color={colors.badgeText.dark}
          isNormalcase
        />
      </Body>
    </CardLink>
  );
};
